import React from 'react';
import {
  FileText,
  Euro,
  TrendingUp,
  Target,
  Download,
  Loader2,
} from 'lucide-react';
import { KpiCard } from '../../societies/components/tabs/dashboard/KpiCard';
import { DashboardFilters } from './DashboardFilters';
import { EvolutionChart } from './EvolutionChart';
import { GuaranteeFormulaCard } from './GuaranteeFormulaCard';
import { AfterSalesSection } from './AfterSalesSection';
import { useAuthStore } from '../../../stores/auth-store';

import {
  DashboardPeriod,
  type ExternalDashboardFilters,
  type ExternalUserDashboardData,
  type PeriodKpi,
} from '../types/dashboard.types';

const PERIOD_CHART_LABELS: Record<string, string> = {
  [DashboardPeriod.DAY]: "aujourd'hui",
  [DashboardPeriod.WEEK]: 'cette semaine',
  [DashboardPeriod.MONTH]: 'ce mois',
  [DashboardPeriod.YEAR]: 'cette année',
  [DashboardPeriod.LAST_12_MONTHS]: '12 derniers mois',
  [DashboardPeriod.ALL]: 'depuis le début',
  [DashboardPeriod.CUSTOM]: 'période sélectionnée',
};

interface DashboardContentProps {
  data: ExternalUserDashboardData;
  filters: ExternalDashboardFilters;
  onFiltersChange: (filters: ExternalDashboardFilters) => void;
  isLoading?: boolean;
  wording: { singularLower: string; pluralLower: string };
  /** Masquer les filtres inline (quand ils sont gérés par le parent) */
  hideFilters?: boolean;
  onExportContracts?: () => void;
  onExportSav?: () => void;
  exportingContracts?: boolean;
  exportingSav?: boolean;
  /** Société avec garant externe + transferPrice activé */
  isGuarantorTransfer?: boolean;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(value);

function getCurrentPeriodData(
  data: ExternalUserDashboardData,
  period?: string,
): PeriodKpi {
  switch (period) {
    case DashboardPeriod.DAY:
      return data.kpis.day;
    case DashboardPeriod.WEEK:
      return data.kpis.week;
    case DashboardPeriod.MONTH:
      return data.kpis.month;
    case DashboardPeriod.YEAR:
      return data.kpis.year;
    case DashboardPeriod.ALL:
      return data.kpis.total;
    case DashboardPeriod.LAST_12_MONTHS:
      return data.kpis.last12Months;
    case DashboardPeriod.CUSTOM:
      // `custom` est absent tant que l'API n'a pas de borne exploitable : on
      // retombe alors sur les 12 derniers mois, comme pour une période inconnue.
      return data.kpis.custom ?? data.kpis.last12Months;
    default:
      return data.kpis.last12Months;
  }
}

export const DashboardContent: React.FC<DashboardContentProps> = ({
  data,
  filters,
  onFiltersChange,
  isLoading = false,
  wording,
  hideFilters = false,
  onExportContracts,
  onExportSav,
  exportingContracts = false,
  exportingSav = false,
  isGuarantorTransfer = false,
}) => {
  const { hasPermission, currentUser } = useAuthStore();
  const canViewContracts = hasPermission('dashboard.view_contracts');
  const canViewRevenue = hasPermission('dashboard.view_revenue');
  const canViewSavClaims = hasPermission('dashboard.view_sav_claims');
  const canViewSavAmounts = hasPermission('dashboard.view_sav_amounts');
  const showCommercialSection = canViewContracts || canViewRevenue;
  const showSavSection = !isGuarantorTransfer && (canViewSavClaims || canViewSavAmounts);
  const savFirst = currentUser?.roleType === 'SAV_MANAGER' || currentUser?.roleType === 'SAV_COORDINATOR';

  // Grilles dynamiques selon les permissions
  const revenueKpiCount = isGuarantorTransfer ? 2 : (data.commissionEnabled ? 3 : 2);
  const kpiCount = (canViewContracts ? 2 : 0) + (canViewRevenue ? revenueKpiCount : 0);
  const kpiGridCols = { 2: 'lg:tw-grid-cols-2', 3: 'lg:tw-grid-cols-3', 4: 'lg:tw-grid-cols-4', 5: 'lg:tw-grid-cols-5', 6: 'lg:tw-grid-cols-6' }[kpiCount] || 'lg:tw-grid-cols-4';
  const chartGridCols = canViewContracts && canViewRevenue ? 'lg:tw-grid-cols-2' : 'lg:tw-grid-cols-1';

  const currentPeriodData = getCurrentPeriodData(data, filters.period);
  const periodLabel =
    PERIOD_CHART_LABELS[filters.period || DashboardPeriod.LAST_12_MONTHS];

  return (
    <div>
      {/* ─── Filtres globaux ─── */}
      {!hideFilters && (
        <DashboardFilters
          filters={filters}
          onFiltersChange={onFiltersChange}
          stores={data.stores}
          availableUsers={data.availableUsers}
          isLoading={isLoading}
        />
      )}

      {(() => {
        const commercialSection = showCommercialSection && (
      <div className="tw-mb-4" key="commercial">
        <div className="tw-flex tw-items-center tw-justify-between tw-bg-affinicar-primary tw-text-white tw-uppercase tw-font-bold tw-px-4 tw-py-4 tw-rounded-lg tw-mb-4 tw-min-h-[56px]">
          <div className="tw-flex tw-items-center tw-gap-2">
            <TrendingUp className="tw-w-4 sm:tw-w-5 tw-h-4 sm:tw-h-5 tw-shrink-0" />
            <span className="tw-text-sm sm:tw-text-base tw-font-bold tw-leading-none">Mon activité commerciale</span>
          </div>
          {onExportContracts && canViewRevenue && (
            <button
              onClick={onExportContracts}
              disabled={exportingContracts}
              className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-bg-white/20 tw-rounded-md tw-text-sm tw-font-medium tw-normal-case hover:tw-bg-white/30 tw-transition-colors disabled:tw-opacity-50"
            >
              {exportingContracts ? <Loader2 size={14} className="tw-animate-spin" /> : <Download size={14} />}
              <span className="tw-hidden sm:tw-inline">Export</span>
            </button>
          )}
        </div>

        {/* KPI Cards */}
        <div
          className={`tw-grid tw-grid-cols-1 md:tw-grid-cols-2 ${kpiGridCols} tw-gap-4 tw-mb-4`}
        >
          {canViewContracts && (
            <KpiCard
              title="Contrats souscrits"
              value={currentPeriodData.totalContracts}
              subtitle={currentPeriodData.label}
              icon={FileText}
              color="affinicar"
            />
          )}
          {canViewContracts && (
            <KpiCard
              title="Contrats vendus"
              value={currentPeriodData.soldContracts}
              subtitle={`${currentPeriodData.soldPercent}% des contrats`}
              icon={Target}
              color="green"
            />
          )}
          {canViewRevenue && !isGuarantorTransfer && (
            <KpiCard
              title={`CA ${wording.pluralLower}`}
              value={formatCurrency(currentPeriodData.revenue)}
              subtitle={currentPeriodData.label}
              icon={Euro}
              color="green"
            />
          )}
          {canViewRevenue && !isGuarantorTransfer && (
            <KpiCard
              title="CA moyen / vente"
              value={formatCurrency(currentPeriodData.averageWarrantyPrice)}
              subtitle={currentPeriodData.label}
              icon={TrendingUp}
              color="affinicar"
            />
          )}
          {canViewRevenue && !isGuarantorTransfer && !!data.commissionEnabled && (
            <KpiCard
              title="Commissions"
              value={formatCurrency(currentPeriodData.commissions)}
              subtitle={`Moy : ${formatCurrency(currentPeriodData.averageCommission)}`}
              icon={TrendingUp}
              color="purple"
            />
          )}
          {canViewRevenue && isGuarantorTransfer && (
            <KpiCard
              title="Rétrocessions"
              value={formatCurrency(data.warrantyFormulas.reduce((sum, f) => sum + f.transferPriceTotal, 0))}
              subtitle={currentPeriodData.label}
              icon={Euro}
              color="green"
            />
          )}
          {canViewRevenue && isGuarantorTransfer && (
            <KpiCard
              title="Refacturations"
              value={formatCurrency(data.warrantyFormulas.reduce((sum, f) => sum + f.memberTransferPriceTotal, 0))}
              subtitle={currentPeriodData.label}
              icon={Euro}
              color="orange"
            />
          )}
        </div>

        {/* Graphiques d'évolution */}
        <div className={`tw-grid tw-grid-cols-1 ${chartGridCols} tw-gap-4 tw-mb-4`}>
          {canViewContracts && (
            <EvolutionChart
              title={`Évolution des ventes (${periodLabel})`}
              data={data.monthlyGraphs.sales}
              series={[
                {
                  dataKey: 'totalContracts',
                  name: 'Total contrats',
                  color: '#8074af',
                },
                {
                  dataKey: 'soldContracts',
                  name: 'Contrats vendus',
                  color: '#10b981',
                },
              ]}
            />
          )}
          {canViewRevenue && (
            <EvolutionChart
              title={isGuarantorTransfer ? `Évolution rétrocessions / refacturations (${periodLabel})` : `Évolution du CA (${periodLabel})`}
              data={data.monthlyGraphs.sales}
              leftAxisFormatter={formatCurrency}
              series={isGuarantorTransfer ? [
                {
                  dataKey: 'transferPrice',
                  name: 'Rétrocessions',
                  color: '#10b981',
                  isCurrency: true,
                },
                {
                  dataKey: 'memberTransferPrice',
                  name: 'Refacturations',
                  color: '#f97316',
                  isCurrency: true,
                },
              ] : [
                {
                  dataKey: 'revenue',
                  name: 'CA généré',
                  color: '#10b981',
                  isCurrency: true,
                },
              ]}
            />
          )}
        </div>

        {/* Détail par formule de garantie */}
        {canViewContracts && data.warrantyFormulas.length > 0 && (
          <div>
            {/* Mobile : titre + cards */}
            <div className="md:tw-hidden">
              <h3 className="tw-text-base sm:tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-3">
                Détail par formule de {wording.singularLower}
              </h3>
              <div className="tw-grid tw-grid-cols-1 tw-gap-4">
                {data.warrantyFormulas.map((formula) => (
                  <GuaranteeFormulaCard
                    key={`${formula.formulaName}-${formula.duration}`}
                    formula={formula}
                    showRevenue={canViewRevenue}
                    isTransferPriceMode={isGuarantorTransfer}
                  />
                ))}
              </div>
            </div>

            {/* Desktop : tableau */}
            <div className="tw-hidden md:tw-block tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-overflow-x-auto tw-max-w-full">
              <table className="tw-w-full tw-text-sm">
                <thead>
                  <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-200">
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-text-base tw-font-semibold tw-text-gray-900">Détail par formule de {wording.singularLower}</th>
                    <th className="tw-px-3 tw-py-3 tw-text-center tw-font-semibold tw-text-gray-700">Durée</th>
                    <th className="tw-px-3 tw-py-3 tw-text-right tw-font-semibold tw-text-gray-700">Volume</th>
                    <th className="tw-px-3 tw-py-3 tw-text-right tw-font-semibold tw-text-gray-700">% vendus</th>
                    {canViewRevenue && !isGuarantorTransfer && <th className="tw-px-3 tw-py-3 tw-text-right tw-font-semibold tw-text-gray-700">Prix moyen</th>}
                    {canViewRevenue && <th className="tw-px-3 tw-py-3 tw-text-right tw-font-semibold tw-text-gray-700">{isGuarantorTransfer ? 'Rétro. totale' : 'CA total'}</th>}
                    {canViewRevenue && isGuarantorTransfer && <th className="tw-px-3 tw-py-3 tw-text-right tw-font-semibold tw-text-orange-700">Refact. totale</th>}
                    <th className="tw-px-3 tw-py-3 tw-text-right tw-font-semibold tw-text-gray-700">KM moyen</th>
                    <th className="tw-px-3 tw-py-3 tw-text-right tw-font-semibold tw-text-gray-700">Âge moyen</th>
                    <th className="tw-px-3 tw-py-3 tw-text-right tw-font-semibold tw-text-gray-700">Prix véhicule</th>
                  </tr>
                </thead>
                <tbody className="tw-divide-y tw-divide-gray-100">
                  {data.warrantyFormulas.map((formula) => (
                    <tr key={`${formula.formulaName}-${formula.duration}`} className="hover:tw-bg-gray-50">
                      <td className="tw-px-4 tw-py-3 tw-font-semibold tw-text-gray-900">{formula.formulaName}</td>
                      <td className="tw-px-3 tw-py-3 tw-text-center">
                        <span className="tw-text-xs tw-font-medium tw-bg-affinicar-lighter tw-text-affinicar-primary tw-px-2 tw-py-0.5 tw-rounded-full">
                          {formula.duration} mois
                        </span>
                      </td>
                      <td className="tw-px-3 tw-py-3 tw-text-right tw-font-bold tw-text-gray-900">{formula.volume}</td>
                      <td className="tw-px-3 tw-py-3 tw-text-right tw-text-green-600 tw-font-medium">{formula.soldPercent}%</td>
                      {canViewRevenue && !isGuarantorTransfer && <td className="tw-px-3 tw-py-3 tw-text-right tw-text-gray-900">{formatCurrency(formula.averagePrice)}</td>}
                      {canViewRevenue && <td className="tw-px-3 tw-py-3 tw-text-right tw-font-semibold tw-text-green-700">{formatCurrency(isGuarantorTransfer ? formula.transferPriceTotal : formula.revenue)}</td>}
                      {canViewRevenue && isGuarantorTransfer && <td className="tw-px-3 tw-py-3 tw-text-right tw-font-semibold tw-text-orange-700">{formatCurrency(formula.memberTransferPriceTotal)}</td>}
                      <td className="tw-px-3 tw-py-3 tw-text-right tw-text-gray-900">
                        {formula.averageKm > 0 ? new Intl.NumberFormat('fr-FR').format(formula.averageKm) + ' km' : '—'}
                      </td>
                      <td className="tw-px-3 tw-py-3 tw-text-right tw-text-gray-900">
                        {formula.averageVehicleAge > 0 ? `${formula.averageVehicleAge} ans` : '—'}
                      </td>
                      <td className="tw-px-3 tw-py-3 tw-text-right tw-text-gray-900">
                        {formula.averageVehiclePrice > 0 ? formatCurrency(formula.averageVehiclePrice) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="tw-bg-gray-50 tw-border-t tw-border-gray-300 tw-font-bold">
                    <td className="tw-px-4 tw-py-3 tw-text-gray-900">Total</td>
                    <td className="tw-px-3 tw-py-3"></td>
                    <td className="tw-px-3 tw-py-3 tw-text-right tw-text-gray-900">
                      {data.warrantyFormulas.reduce((sum, f) => sum + f.volume, 0)}
                    </td>
                    <td className="tw-px-3 tw-py-3 tw-text-right tw-text-green-600">
                      {(() => {
                        const totalVol = data.warrantyFormulas.reduce((s, f) => s + f.volume, 0);
                        const totalSold = data.warrantyFormulas.reduce((s, f) => s + f.soldCount, 0);
                        return totalVol > 0 ? Math.round((totalSold / totalVol) * 100) : 0;
                      })()}%
                    </td>
                    {canViewRevenue && !isGuarantorTransfer && (
                      <td className="tw-px-3 tw-py-3 tw-text-right tw-text-gray-900">
                        {(() => {
                          const totalSold = data.warrantyFormulas.reduce((s, f) => s + f.soldCount, 0);
                          const totalRevenue = data.warrantyFormulas.reduce((s, f) => s + f.revenue, 0);
                          return totalSold > 0 ? formatCurrency(totalRevenue / totalSold) : '—';
                        })()}
                      </td>
                    )}
                    {canViewRevenue && (
                      <td className="tw-px-3 tw-py-3 tw-text-right tw-text-green-700">
                        {formatCurrency(isGuarantorTransfer
                          ? data.warrantyFormulas.reduce((sum, f) => sum + f.transferPriceTotal, 0)
                          : data.warrantyFormulas.reduce((sum, f) => sum + f.revenue, 0)
                        )}
                      </td>
                    )}
                    {canViewRevenue && isGuarantorTransfer && (
                      <td className="tw-px-3 tw-py-3 tw-text-right tw-font-semibold tw-text-orange-700">
                        {formatCurrency(data.warrantyFormulas.reduce((sum, f) => sum + f.memberTransferPriceTotal, 0))}
                      </td>
                    )}
                    <td className="tw-px-3 tw-py-3 tw-text-right tw-text-gray-900">
                      {(() => {
                        const withKm = data.warrantyFormulas.filter(f => f.averageKm > 0);
                        if (withKm.length === 0) return '—';
                        const totalKm = withKm.reduce((s, f) => s + f.averageKm * f.volume, 0);
                        const totalVol = withKm.reduce((s, f) => s + f.volume, 0);
                        return new Intl.NumberFormat('fr-FR').format(Math.round(totalKm / totalVol)) + ' km';
                      })()}
                    </td>
                    <td className="tw-px-3 tw-py-3 tw-text-right tw-text-gray-900">
                      {(() => {
                        const withAge = data.warrantyFormulas.filter(f => f.averageVehicleAge > 0);
                        if (withAge.length === 0) return '—';
                        const totalAge = withAge.reduce((s, f) => s + f.averageVehicleAge * f.volume, 0);
                        const totalVol = withAge.reduce((s, f) => s + f.volume, 0);
                        return `${Math.round(totalAge / totalVol)} ans`;
                      })()}
                    </td>
                    <td className="tw-px-3 tw-py-3 tw-text-right tw-text-gray-900">
                      {(() => {
                        const withPrice = data.warrantyFormulas.filter(f => f.averageVehiclePrice > 0);
                        if (withPrice.length === 0) return '—';
                        const totalPrice = withPrice.reduce((s, f) => s + f.averageVehiclePrice * f.volume, 0);
                        const totalVol = withPrice.reduce((s, f) => s + f.volume, 0);
                        return formatCurrency(Math.round(totalPrice / totalVol));
                      })()}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}
      </div>
        );

        const savSection = showSavSection && (
          <div className="tw-mb-4 tw-pt-4" key="sav">
            <AfterSalesSection savReport={data.savReport} periodLabel={periodLabel} breakdownAnalysis={data.breakdownAnalysis} onExportSav={onExportSav} exportingSav={exportingSav} />
          </div>
        );

        return savFirst
          ? <>{savSection}{commercialSection}</>
          : <>{commercialSection}{savSection}</>;
      })()}
    </div>
  );
};
